import {forwardRef, useState} from "react";
import {Box, BoxProps, Text} from "@mantine/core";
import {IconChevronDown, IconChevronUp} from "@tabler/icons-react";
import {MarkdownEditor, TMarkdownEditingMode} from "@/common/components/MarkdownEditor";
import classes from "./Instructions.module.css";

interface InstructionsProps extends BoxProps {
    instructions: string;
    editingMode?: TMarkdownEditingMode;
    onChange?: (instructions: string) => void;
    expanded?: boolean;
}

/**
 * A collapsible instructions field component that displays step instructions
 * using a MarkdownEditor. Acts like an EventRecorder with collapsed/expanded states.
 *
 * In planning mode: always shown below row1, editable
 * In non-planning mode: shown only if instructions is defined and non-empty, readonly
 */
export const Instructions = forwardRef<HTMLDivElement, InstructionsProps>(({
    instructions,
    editingMode = "none",
    expanded = false,
    onChange,
    ...others
}, ref) => {
    const [isExpanded, setIsExpanded] = useState(expanded);

    const handleInstructionsChange = (newInstructions: string) => {
        onChange?.(newInstructions);
    };

    return (
        <Box ref={ref} className={classes.container} {...others}>
            {isExpanded ? (
                <Box className={classes.expanded}>
                    <Box className={classes.collapseButton} onClick={() => setIsExpanded(false)}>
                        <Text size={"xs"}>Instructions</Text>
                        <IconChevronUp size="1em"/>
                    </Box>
                    <MarkdownEditor
                        markdown={instructions || ""}
                        onChange={handleInstructionsChange}
                        editingMode={editingMode}
                        mdDataTestId="step-instructions"
                    />
                </Box>
            ) : (
                <Box className={classes.collapsedBar} onClick={() => setIsExpanded(true)}>
                    <Box className={classes.collapseButton}>
                        <Text size={"xs"}>Instructions</Text>
                        <IconChevronDown size="1em"/>
                    </Box>
                </Box>
            )}
        </Box>
    );
});